import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '~/components/auth/ProtectedRoute';
import AppLayout from '~/components/layout/AppLayout';
import IndexPage from '~/pages/Index';
import ChatPage from '~/pages/Chat';
import ProjectsPage from '~/pages/Projects';
import ProjectDetailPage from '~/pages/ProjectDetail';
import SharedProjectPage from '~/pages/SharedProject';
import SharedWorkPage from '~/pages/SharedWork';
import ClaimPage from '~/pages/Claim';
import HandedOffChatPage from '~/pages/HandedOffChat';
import NotFoundPage from '~/pages/NotFound';
import WelcomePage from '~/pages/Welcome';
import DesktopAuthPage from '~/pages/DesktopAuth';
import DesktopCheckoutPage from '~/pages/DesktopCheckout';
import DesktopLinearPage from '~/pages/DesktopLinear';
import SettingsBillingPage from '~/pages/SettingsBilling';
import RemotePage from '~/pages/Remote';
import DomainsPage from '~/pages/Domains';
import DomainDetailPage from '~/pages/DomainDetail';
import DevelopersPage from '~/pages/Developers';
import MarketplaceReviewPage from '~/pages/MarketplaceReview';
import DevPreviewIdle from '~/pages/_DevPreviewIdle'; // TEMP dev-only route
import DevBuildActivity from '~/pages/_DevBuildActivity'; // TEMP dev-only route
import DevThread from '~/pages/_DevThread'; // TEMP dev-only route
import DevHeader from '~/pages/_DevHeader'; // TEMP dev-only route
import AffiliateLayout from '~/pages/affiliate/AffiliateLayout';
import AffiliateOverview from '~/pages/affiliate/Overview';
import AffiliateReferrals from '~/pages/affiliate/Referrals';
import AffiliateEarnings from '~/pages/affiliate/Earnings';
import AffiliatePayouts from '~/pages/affiliate/Payouts';
import AffiliateResources from '~/pages/affiliate/Resources';
import AffiliateCapturePage from '~/pages/AffiliateCapture';
import { usePostHogPageview } from '~/lib/hooks/usePostHogPageview';
import { useCheckoutReturn } from '~/lib/hooks/useCheckoutReturn';
import { useClaimIntentResume } from '~/lib/hooks/useClaimIntentResume';
import { useStorefrontLinks } from '~/lib/hooks/useStorefrontLinks';
import { PlanWelcomeModal } from '~/components/account/PlanWelcomeModal';

export default function AppRoutes() {
  usePostHogPageview();
  useCheckoutReturn();
  // Sends a freshly signed-in user back to a claim link they started before
  // auth (survives the magic-link redirect-to-root) — see DEV-170.
  useClaimIntentResume();
  // dunea.ai storefront deep links (?marketplace=, ?remix=, ?expert=, ?wishlist=, ?wish=, &ref=).
  useStorefrontLinks();

  return (
    <>
      {/* Global celebration shown when a paid plan goes active (checkout return / upgrade). */}
      <PlanWelcomeModal />
      <Routes>
        {/* Legacy auth routes — redirect to home (auth is now a modal) */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/register" element={<Navigate to="/" replace />} />

        {/*
         * Post-signup waypoint. Auth is a modal, so this is the only URL unique
         * to a brand-new account — which is what Google/Meta need to attribute a
         * conversion. Fires the pixel, then bounces to "/".
         */}
        <Route path="/welcome" element={<WelcomePage />} />

        {/* TEMP dev-only preview idle harness (public, no auth) */}
        <Route path="/dev/preview-idle" element={<DevPreviewIdle />} />
        <Route path="/dev/build-activity" element={<DevBuildActivity />} />
        <Route path="/dev/thread" element={<DevThread />} />
        <Route path="/dev/header" element={<DevHeader />} />

        {/* Home is public — auth modal handles login inline */}
        <Route element={<AppLayout />}>
          <Route path="/" element={<IndexPage />} />
        </Route>

        {/* Chat requires auth (user will already be authed via the modal) */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/chat/:id" element={<ChatPage />} />
          {/* Dunea Desktop/Work's "Manage on website": opens Settings → Plan */}
          <Route path="/settings/billing" element={<SettingsBillingPage />} />
          {/* Admin read-only archive of a handed-off (claimed) generation. */}
          <Route path="/handed-off/:chatId" element={<HandedOffChatPage />} />
        </Route>

        {/* These pages have their own layouts */}
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:id"
          element={
            <ProtectedRoute>
              <ProjectDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/remote"
          element={
            <ProtectedRoute>
              <RemotePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/domains"
          element={
            <ProtectedRoute>
              <DomainsPage />
            </ProtectedRoute>
          }
        />
        {/* Marketplace developer portal (plugins + themes) and the admin review queue. */}
        <Route
          path="/developers"
          element={
            <ProtectedRoute>
              <DevelopersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/developers/review"
          element={
            <ProtectedRoute>
              <MarketplaceReviewPage />
            </ProtectedRoute>
          }
        />
        {/* Affiliate partner portal — sidebar shell with nested sections. */}
        <Route
          path="/affiliates"
          element={
            <ProtectedRoute>
              <AffiliateLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AffiliateOverview />} />
          <Route path="referrals" element={<AffiliateReferrals />} />
          <Route path="earnings" element={<AffiliateEarnings />} />
          <Route path="payouts" element={<AffiliatePayouts />} />
          <Route path="resources" element={<AffiliateResources />} />
        </Route>
        <Route
          path="/domains/:domain"
          element={
            <ProtectedRoute>
              <DomainDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/desktop-auth"
          element={
            <ProtectedRoute stayOnPage>
              <DesktopAuthPage />
            </ProtectedRoute>
          }
        />
        {/* Dodo's return page for a Dunea Desktop plan — public: it only hands off to the app */}
        <Route path="/desktop-checkout" element={<DesktopCheckoutPage />} />
        <Route
          path="/desktop-linear"
          element={
            <ProtectedRoute stayOnPage>
              <DesktopLinearPage />
            </ProtectedRoute>
          }
        />
        <Route path="/shared/project/:id" element={<SharedProjectPage />} />
        {/* A Dunea Work chat someone shared — public, read-only */}
        <Route path="/share/:id" element={<SharedWorkPage />} />
        {/* Public claim landing — the recipient may have no account yet (DEV-170). */}
        <Route path="/claim/:token" element={<ClaimPage />} />

        {/* Public referral capture — drops the first-touch cookie, then redirects home */}
        <Route path="/r/:code" element={<AffiliateCapturePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
